import { useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight } from "react-feather";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import commaFormat from "@utils/commaFormat";
import { toTitleCase } from "@containers/Hermes/utils/companyLinks";
import type { ContactsOfferBlock, ContactsOfferCompany } from "@containers/Hermes/types";
import type { ResultsSource } from "@containers/Hermes/hooks/useActionPanel";
import useResultsRows, { PAGE_SIZES } from "@containers/Hermes/hooks/useResultsRows";
import BookmarkCard, { toBookmarkCompanies } from "./BookmarkCard";
import ContactsCard from "./ContactsCard";
import DownloadCard from "./DownloadCard";
import { ACTION_LIMITS, overLimitMessage } from "@containers/Hermes/utils/actionLimits";
import LimitTip from "./LimitTip";
import { cachedProfile, profileUrl, resolveCompanyProfile } from "@containers/Hermes/utils/companyProfiles";
import { alignToPreview } from "@containers/Hermes/utils/alignColumns";
import { SkeletonBar, TableSkeleton } from "./Skeleton";
import ResultsTable from "./ResultsTable";
import type { ResultsTableRow } from "./ResultsTable";
import WorkspaceCard from "./WorkspaceCard";

/**
 * The full table behind an answer's table, opened on the right when the user
 * chooses to customise the selection (Zeplin: the "customise selection from all
 * N records" step). It runs the offer's own query a page at a time, so the rows
 * are the ones the preview, the count and a download are made from.
 *
 *   header      N records                                           ✕
 *   table       ☐ S.No  Company  Country …   (25 / 50 / 100 per page)
 *   pager       1–25 of 5,400            ‹ 1 2 3 ›
 *   footer      nothing selected: Download
 *               rows selected:    Bookmark | Add to workspace | Request contacts
 *
 * The action modals are the action cards themselves, in their dialog form. The
 * default selection is the companies the answer's table already showed.
 */

const SERIAL = "S.No";
const MAX_CONTACT_COMPANIES = ACTION_LIMITS.contacts;

type Picked = { name: string; country?: string };
type Modal = { kind: "download"; companyIds?: string[]; rowKeys?: string[] } | { kind: "bookmark" | "workspace" | "contacts"; block: ContactsOfferBlock };

type ResultsPanelProps = {
  source: ResultsSource;
  conversationId: string | null;
  onClose: () => void;
};

export default function ResultsPanel({ source, conversationId, onClose }: ResultsPanelProps) {
  const { download, contacts, messageId } = source;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const knownTotal = download.record_count ?? download.matched_records ?? null;
  const rows = useResultsRows(download.spec_token, page, pageSize, knownTotal === null);
  const total = knownTotal ?? rows.total;
  const unitNoun = download.unit_noun || "records";

  // Default selection: the companies the answer's own table showed, matched by id — none when the user
  // chose to pick from all the records afresh.
  const [selected, setSelected] = useState<Map<string, Picked>>(() => {
    const initial = new Map<string, Picked>();
    if (source.startEmpty) return initial;
    for (const company of toBookmarkCompanies(contacts?.companies ?? [])) initial.set(company.company_id, { name: company.name, country: company.country });
    // A table that is not about companies starts from the rows its preview showed, by their bucket keys.
    if (initial.size === 0) for (const key of download.row_keys ?? []) if (key) initial.set(key, { name: key });
    return initial;
  });
  // An offer made before its rows were tied to buckets has no keys to start from. Its table is the top of the
  // result, so the same first rows — as many as the preview showed — start selected, found by their keys.
  const needsSeed = !source.startEmpty && selected.size === 0 && source.previewCount > 0;
  useEffect(() => {
    if (!needsSeed) return undefined;
    let cancelled = false;
    axios
      .post(getApiUrl("downloads/rows"), { spec_token: download.spec_token, page: 1, page_size: Math.min(source.previewCount, 200) })
      .then(({ data: payload }) => {
        const keys: (string | null)[] = payload?.data?.row_keys ?? [];
        const initial = new Map<string, Picked>();
        keys.forEach((key) => {
          if (key) initial.set(key, { name: key });
        });
        // Only when nothing was ticked in the meantime.
        if (!cancelled && initial.size > 0) setSelected((previous) => (previous.size === 0 ? initial : previous));
      })
      .catch((error) => Sentry.captureException(error));
    return () => {
      cancelled = true;
    };
    // Once, when the panel opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [modal, setModal] = useState<Modal | null>(null);
  const [resolving, setResolving] = useState(false);

  const data = rows.data;
  // The columns the rows carry, named and ordered like the answer's preview table.
  const columns = useMemo(
    () =>
      alignToPreview(
        (data?.columns ?? []).filter((column) => column !== SERIAL),
        source.previewHeaders ?? []
      ),
    [data, source.previewHeaders]
  );
  const nameColumn = columns[0]?.key;
  const hasCompanies = Boolean(data?.company_ids.some(Boolean));
  // Rows that are not companies can still be ticked, by the bucket behind each row, and downloaded.
  const hasKeys = !hasCompanies && Boolean(data?.row_keys?.some(Boolean));
  const selectable = hasCompanies || hasKeys;
  const tableData = data && hasKeys ? { ...data, company_ids: data.row_keys ?? [] } : data;

  const pageTotal = total ?? data?.max_size ?? 0;
  const pageCount = Math.max(1, Math.ceil(pageTotal / pageSize));
  const first = pageTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, pageTotal);

  // A company name opens its profile. The profile is looked up when the name is clicked, then remembered.
  const [, setProfilesVersion] = useState(0);
  const linkQuery = useMemo(() => {
    const current = new URLSearchParams(location.search);
    const forwarded = new URLSearchParams();
    for (const name of ["country", "type"]) {
      const value = current.get(name);
      if (value) forwarded.set(name, value);
    }
    return forwarded.toString();
  }, []);
  const knownProfileUrl = (id: string | null | undefined): string | undefined => {
    const profile = id ? cachedProfile(id) : undefined;
    return profile ? profileUrl(profile, linkQuery) : undefined;
  };
  const openProfile = async (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (cachedProfile(id)) return; // its href is already set
    event.preventDefault();
    try {
      const profile = await resolveCompanyProfile(id);
      if (!profile) {
        toast.error("No company profile found in our system.");
        return;
      }
      setProfilesVersion((version) => version + 1);
      // Opened once the profile is known. If the browser has by now stopped treating the click as a
      // user action and blocks the tab, the name's link is already set, so clicking it again opens it.
      // (Not opened with "noopener": that always returns null, which would look like a blocked tab.)
      const tab = window.open(profileUrl(profile, linkQuery), "_blank");
      if (tab) tab.opener = null;
      else toast.info("The profile is ready. Click the name again to open it.");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("The company profile could not be opened. Please try again.");
    }
  };

  // The table reports the rows ticked on its page; the selection spans pages, so only this page's rows change.
  const [selectionEpoch, setSelectionEpoch] = useState(0);
  const countryColumn = columns.find((column) => /country/i.test(column.label))?.key;
  const syncPageSelection = (ticked: ResultsTableRow[], page: ResultsTableRow[]) => {
    const tickedIds = new Set(ticked.map((row) => row.__companyId));
    setSelected((previous) => {
      let changed = false;
      const next = new Map(previous);
      for (const row of page) {
        const id = row.__companyId;
        if (!id) continue;
        if (tickedIds.has(id)) {
          if (!next.has(id)) {
            next.set(id, { name: toTitleCase(String(row[nameColumn] ?? "")), country: countryColumn ? String(row[countryColumn] ?? "") || undefined : undefined });
            changed = true;
          }
        } else if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  };
  const clearSelection = () => {
    setSelected(new Map());
    // The table holds its own tick state; a fresh one picks up the empty selection.
    setSelectionEpoch((epoch) => epoch + 1);
  };

  const selectedBlock = (companies: ContactsOfferCompany[]): ContactsOfferBlock => ({ type: "contacts_offer", label: contacts?.label ?? "", companies, matched_records: companies.length, max_rows: MAX_CONTACT_COMPANIES });
  const asOffered = (): ContactsOfferCompany[] => [...selected.entries()].map(([id, company]) => ({ company_id: id, master_id: id, name: company.name, display: company.name, country: company.country }));

  const openCompanyAction = async (kind: "bookmark" | "workspace" | "contacts") => {
    if (selected.size === 0 || resolving) return;
    if (kind !== "contacts") {
      setModal({ kind, block: selectedBlock(asOffered()) });
      return;
    }
    // Contacts are looked up by the profile's own company id, which the rows do
    // not carry — resolve it from the master ids, and drop any without a profile.
    setResolving(true);
    try {
      const { data: resolved } = await axios.post(getApiUrl("hermes/companies/resolve"), { master_ids: [...selected.keys()].slice(0, MAX_CONTACT_COMPANIES * 4) });
      const byMaster = new Map<string, { company_id: string; country?: string }>((resolved?.companies ?? []).map((entry: { master_id: string; company_id: string; country?: string }) => [entry.master_id, entry]));
      const companies = asOffered()
        .filter((company) => byMaster.has(company.master_id as string))
        .map((company) => ({ ...company, company_id: byMaster.get(company.master_id as string)!.company_id }));
      if (companies.length === 0) {
        toast.error("None of the selected companies have a profile to request contacts for.");
      } else {
        if (companies.length < selected.size) toast.info(`${(selected.size - companies.length).toLocaleString()} selected companies have no profile and were left out.`);
        setModal({ kind: "contacts", block: selectedBlock(companies) });
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error("The companies could not be looked up. Please try again.");
    } finally {
      setResolving(false);
    }
  };

  const closeModal = () => setModal(null);
  const contactsAvailable = Boolean(conversationId) && messageId > 0;
  return (
    <aside aria-label={`All ${unitNoun}`} className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-w-full tw-bg-white">
      <div className="tw-flex tw-items-center tw-gap-3 tw-h-12 tw-flex-shrink-0 tw-px-4 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
        <span className="tw-text-sm tw-font-normal tw-text-gray-900">{total !== null ? `${total.toLocaleString()} results found` : "Results"}</span>
        <button type="button" onClick={onClose} className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5 tw-h-7 tw-px-3 tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50 tw-cursor-pointer">
          <span aria-hidden="true" className="material-symbols-outlined !tw-text-[16px]">
            arrow_back
          </span>
          Go back to chat
        </button>
      </div>

      {/* The line of selections: the count on the left, every action on the right. The
          company actions need a selection. Download takes the selected companies, or —
          with nothing selected — all of the result or a range. */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-flex-shrink-0 tw-min-h-11 tw-px-4 tw-py-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
        {!data && !rows.error ? <SkeletonBar className="tw-h-5 tw-w-24" /> : null}
        {selectable ? (
          <span className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-rounded-md tw-bg-[#EFE9FD] tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-[#5B21D6]">{selected.size.toLocaleString()} selected</span>
            {selected.size > 0 ? (
              <button type="button" onClick={clearSelection} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-xs tw-font-medium tw-text-primary-purple-600 hover:tw-underline tw-cursor-pointer">
                Clear selection
              </button>
            ) : null}
          </span>
        ) : null}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-ml-auto">
          {!data && !rows.error ? (
            // Until the first page arrives the actions are placeholders: which of them apply, and what
            // Download takes (the selected companies or all of the result), depends on the rows.
            <>
              <SkeletonBar className="tw-h-5 tw-w-20" />
              <SkeletonBar className="tw-h-5 tw-w-28" />
              <SkeletonBar className="tw-h-5 tw-w-28" />
              <SkeletonBar className="tw-h-5 tw-w-20" />
            </>
          ) : null}
          {hasCompanies ? (
            <>
              <PanelAction icon="star" label="Bookmark" title={overLimitMessage("bookmark", selected.size)} disabled={resolving || selected.size === 0 || Boolean(overLimitMessage("bookmark", selected.size))} onClick={() => openCompanyAction("bookmark")} />
              <PanelAction icon="workspaces" label="Add to workspace" title={overLimitMessage("workspace", selected.size)} disabled={resolving || selected.size === 0 || Boolean(overLimitMessage("workspace", selected.size))} onClick={() => openCompanyAction("workspace")} />
              {contactsAvailable ? <PanelAction icon="account_box" label="Request contacts" title={overLimitMessage("contacts", selected.size)} disabled={resolving || selected.size === 0 || Boolean(overLimitMessage("contacts", selected.size))} onClick={() => openCompanyAction("contacts")} /> : null}
            </>
          ) : null}
          {data || rows.error ? <PanelAction icon="cloud_download" label="Download" title={selectable ? overLimitMessage("download", selected.size) : undefined} disabled={resolving || (selectable && Boolean(overLimitMessage("download", selected.size)))} onClick={() => setModal(selectable && selected.size > 0 ? (hasKeys ? { kind: "download", rowKeys: [...selected.keys()] } : { kind: "download", companyIds: [...selected.keys()] }) : { kind: "download" })} /> : null}
        </div>
      </div>

      <div className="tw-flex-grow tw-min-h-0 tw-overflow-auto">
        {rows.error ? (
          <div role="alert" className="tw-p-6 tw-text-sm tw-text-red-600">
            {rows.error}{" "}
            <button type="button" onClick={rows.retry} className="tw-border-none tw-bg-transparent tw-p-0 tw-font-medium tw-text-primary-purple-600 tw-cursor-pointer hover:tw-underline">
              Try again
            </button>
          </div>
        ) : !data ? (
          <TableSkeleton />
        ) : (
          <div className={rows.loading ? "tw-opacity-60" : undefined}>
            <ResultsTable key={`${data.offset}:${pageSize}:${selectionEpoch}`} data={tableData ?? data} linkNames={hasCompanies} serialColumn={SERIAL} columns={columns} nameColumn={nameColumn} isSelected={(id) => selected.has(id)} onPageSelection={syncPageSelection} profileHref={(id) => knownProfileUrl(id)} onOpenProfile={openProfile} />
          </div>
        )}
      </div>

      {/* The pager is the shared table's own (same markup and classes, which are global), driven by the
          panel because the rows are paged by the server rather than held by the table. */}
      <div className="tw-flex-shrink-0 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200">
        <div className="trademo-table-pagination-container">
          <div className="trademo-table-pagination-rpp-container">
            <span className="trademo-table-pagination-rpp-text">Result per page </span>
            <select
              className="trademo-table-pagination-rpp-select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}>
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="trademo-table-count-display">
            <span className="trademo-table-count-text">
              {first}-{last} of {commaFormat(pageTotal)}
            </span>
          </div>
          <div className="trademo-table-button-container">
            <button type="button" className="trademo-table-arrow-button" onClick={() => setPage((n) => Math.max(1, n - 1))} disabled={page <= 1 || rows.loading} aria-label="Previous page">
              <ChevronLeft size={16} color="#8C8C8C" />
            </button>{" "}
            <button type="button" className="trademo-table-arrow-button" onClick={() => setPage((n) => Math.min(pageCount, n + 1))} disabled={page >= pageCount || rows.loading} aria-label="Next page">
              <ChevronRight size={16} color="#8C8C8C" />
            </button>
          </div>
        </div>
      </div>

      {modal?.kind === "download" ? <DownloadCard modal block={download} companyIds={modal.companyIds} rowKeys={modal.rowKeys} onClose={closeModal} /> : null}
      {modal?.kind === "bookmark" ? <BookmarkCard modal block={modal.block} onClose={closeModal} /> : null}
      {modal?.kind === "workspace" ? <WorkspaceCard modal block={modal.block} messageId={messageId} onClose={closeModal} /> : null}
      {modal?.kind === "contacts" && conversationId ? <ContactsCard modal block={modal.block} conversationId={conversationId} messageId={messageId} onClose={closeModal} /> : null}
    </aside>
  );
}

function PanelAction({ icon, label, title, disabled, onClick }: { icon: string; label: string; title?: string; disabled: boolean; onClick: () => void }) {
  return (
    <LimitTip tip={title}>
      <button type="button" onClick={onClick} disabled={disabled} className="tw-flex tw-items-center tw-gap-1 tw-h-9 tw-p-0 tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-text-primary-purple-500 hover:tw-text-primary-purple-600 tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
        <span aria-hidden="true" className="material-symbols-outlined tw-text-[20px] tw-leading-none tw-select-none">
          {icon}
        </span>
        {label}
      </button>
    </LimitTip>
  );
}
